import React, { useMemo, useState, useEffect, useRef } from 'react';
import ZombiePurgeHeader from './Header';
import ZombiePurgeConnectWallet from './ConnectWallet';
import ZombiePurgeList from './List';
import ZombiePurgeModal from './Modal/ZombiePurgeModal';
import styles from './ZombiePurgeView.module.scss';
import ZombiePurgeSuccess from './Success/ZombiePurgeSuccess';
import ZombiePurgeAnimation from './ZombiePurgeAnimation/ZombiePurgeAnimation';
import LoadingOverlay from 'src/ui/components/LoadingOverlay';
import { CONTRACT_NAMES } from 'src/base/constants';
import gsap from 'gsap';

// hooks
import useZombiePurge from 'src/ui/views/ZombiePurge/useZombiePurge';
import useContract from 'src/hooks/useContract';
import { useRouter } from 'next/router';


// TODO: consider delegation


const PurgingStates = {
	READY: 'ready',
	TRANSACTION_PROCESSING: 'TRANSACTION_PROCESSING',
	TRANSACTION_CONFIRMED: 'TRANSACTION_CONFIRMED',
	COMPLETED: 'COMPLETED',
	SUCCESS: 'SUCCESS',
};

const animationDurationMs = 49000; // in milliseconds

// TODO: this should not import zombieContract. useZombiePurge should supply the necessary methods

export default function ZombiePurgeView(props) {
	const router = useRouter();
	const { connect, address, network, provider, viewPurged: viewPurgedInQuery } = props;
	const [showModal, setShowModal] = useState(false);
	const [numberOfZombiesToPurge, setNumberOfZombiesToPurge] = useState(0);
	const [zombiesToPurge, setZombiesToPurge] = useState([]);
	const [loadingText, setLoadingText] = useState('Loading Zombies...');
	const [addressHasApprovedLootMintContract, setAddressHasApprovedLootMintContract] = useState(null);
	const [approvalIsProcessing, setApprovalIsProcessing] = useState(false);
	const [purgingState, setPurgingState] = useState(PurgingStates.READY);
	const zombieContract = useContract(provider, network, CONTRACT_NAMES.ZAYC);
	const lootContract = useContract(provider, network, CONTRACT_NAMES.LOOT, true);
	const lootMinterContract = useContract(provider, network, CONTRACT_NAMES.LOOT_MINTER);
	const connected = useMemo(() => {
		return !!(address && provider);
	}, [address, provider]);
	const { zombies, lootBalances, loading: zombieBalanceLoading, purgeStart } = useZombiePurge(address, zombieContract, lootContract);
	const purgeStarted = true; // no longer using the value from useZombiePurge
	const [replayAnimationRequested, setReplayAnimationRequested] = useState(false);
	const showLoadingOverlay = useMemo(() => {

		return purgingState === PurgingStates.TRANSACTION_PROCESSING || approvalIsProcessing || zombieBalanceLoading;

	}, [purgingState, approvalIsProcessing, zombieBalanceLoading]);

	const [otherAddress, setOtherAddress] = useState('');
	const [isOtherAddressValid, setIsOtherAddressValid] = useState(false);

	const zombiePurgePageRef = useRef(null);
	const isOtherAddressSimilarToConnectedAddress = String(address).toLowerCase() === String(otherAddress).toLowerCase();


	// to fix screen getting vertically cut off
	// this is to counter what is being done in layout.js
	useEffect(() => {
		if (zombiePurgePageRef.current) {
			document.body.style.minHeight = `${zombiePurgePageRef.current.offsetHeight + 300}px`;
		}
	}, [zombiePurgePageRef, showLoadingOverlay, zombies]);



	const onListPurgeClick = (selectedZombies) => {
		setNumberOfZombiesToPurge(selectedZombies.length);
		setZombiesToPurge(selectedZombies);
		setShowModal(true);
	};

	const playAnimation = () => {
		setPurgingState(PurgingStates.TRANSACTION_CONFIRMED); // this triggers the animation
		gsap.to(window, { duration: 1, scrollTo: { y: '#zombie-purge-animation', offsetY: 0 } }); // updating id
		// return resolved promise after animation is done
		return new Promise((resolve) => {
			setTimeout(() => {
				setPurgingState(PurgingStates.COMPLETED); // this ends the animation
				const updatedQuery = { ...router.query, viewPurged: 'true', purged_count: numberOfZombiesToPurge };
				router.push({ pathname: router.pathname, query: updatedQuery });
				setShowModal(false);
				resolve();
			}, animationDurationMs);
		});
	};

	const purgeZombies = async (_zombiesToPurge, _address) => {
		const tx = await lootMinterContract.mint0(_zombiesToPurge, _address);
		await tx.wait();
	};

	const onClickPurge = async () => {
		setPurgingState(PurgingStates.TRANSACTION_PROCESSING);
		setLoadingText('Processing transaction...');

		const targetAddress = isOtherAddressValid && otherAddress ? otherAddress : address;
		console.log('target address:', targetAddress);

		try {
			await purgeZombies(zombiesToPurge, targetAddress);
			await playAnimation();
			// scrolling to success state container
		}
		catch (e) {
			console.error(e);
			setLoadingText('Loading Zombies...');
			setPurgingState(PurgingStates.READY);
			alert(`Something went wrong. Details: ${e.message}`);
		}
	};

	const onClickApprove = async () => {
		setApprovalIsProcessing(true);
		setLoadingText('Processing transaction...');
		try {
			const tx = await zombieContract.setApprovalForAll(lootMinterContract.address, true);
			await tx.wait();
			setApprovalIsProcessing(false);
			setAddressHasApprovedLootMintContract(true);
		} catch (error) {
			setLoadingText('Loading Zombies..');
			setApprovalIsProcessing(false);
			console.error(error);
		}

	};

	const purgedZombiesCount = Number(lootBalances[0]?.balance);

	// decide whether to show success component
	useEffect(() => {
		if (viewPurgedInQuery && purgedZombiesCount > 0) {
			setPurgingState(PurgingStates.COMPLETED);
		} else if (viewPurgedInQuery && (lootBalances && purgedZombiesCount < 1)) {
			resetPage();
		}
	}, [viewPurgedInQuery, purgedZombiesCount]);

	useEffect(() => {

		if (!zombieContract) {
			return;
		}

		// asset to be burned 
		if (zombieContract && !lootMinterContract?.address) {
			throw new Error('loot minter contract not found');
		}
		if (!address) {
			return;
		}

		const checkApproval = async () => {
			const isApproved = await zombieContract.isApprovedForAll(address, lootMinterContract.address);
			setAddressHasApprovedLootMintContract(isApproved);
		};
		checkApproval();
	}, [lootMinterContract, zombieContract, address]);

	const shouldPlayAnimation = useMemo(() => {
		return purgingState === PurgingStates.TRANSACTION_CONFIRMED || replayAnimationRequested;
	}, [replayAnimationRequested, purgingState]);

	function resetPage() {
		router.push('/zombie-purge', undefined, { shallow: true });
		setPurgingState(PurgingStates.READY);
	}

	const replayAnimation = async () => {
		setReplayAnimationRequested(true);
		await playAnimation();
		setReplayAnimationRequested(false);
		gsap.to(window, { duration: 1, scrollTo: { y: '#zombie-purge-animation', offsetY: 0 } });
	};


	return (
		<div className={'relative border-box h-auto w-full pb-6'}
			ref={zombiePurgePageRef}>
			{shouldPlayAnimation && <ZombiePurgeAnimation />}
			{<LoadingOverlay show={showLoadingOverlay}
				text={loadingText} />}
			<ZombiePurgeHeader
				purgeStarted={purgeStarted}
				purgeStart={purgeStart} />
			{connected && (purgingState !== PurgingStates.COMPLETED) && purgeStarted && (
				<ZombiePurgeList
					onListPurgeClick={onListPurgeClick}
					className='z-[2]'
					zombies={zombies}
					otherAddress={!isOtherAddressSimilarToConnectedAddress && otherAddress}
					setOtherAddress={setOtherAddress}
					isOtherAddressValid={isOtherAddressValid}
					setIsOtherAddressValid={setIsOtherAddressValid}
					isOtherAddressSimilarToConnectedAddress={isOtherAddressSimilarToConnectedAddress}
				/>
			)}
			{connected && (purgingState === PurgingStates.COMPLETED) && (
				<ZombiePurgeSuccess {...props}
					purgedZombiesCount={purgedZombiesCount || zombiesToPurge.length}
					onClickReplay={replayAnimation}
					resetPage={resetPage} />
			)}

			{connected && showModal && (
				<ZombiePurgeModal
					key='zombie-purge-modal'
					onModalClose={() => setShowModal(false)}
					addressHasApprovedLootMintContract={addressHasApprovedLootMintContract}
					className='z-[3]'
					onApproveClick={onClickApprove}
					onPurgeClick={onClickPurge}
					numberOfZombiesSelected={numberOfZombiesToPurge}
					otherAddress={(isOtherAddressValid && !isOtherAddressSimilarToConnectedAddress !== address) ? otherAddress : undefined}
					isOtherAddressSimilarToConnectedAddress={isOtherAddressSimilarToConnectedAddress}
				/>
			)}

			{!connected && (
				<ZombiePurgeConnectWallet
					className='z-[2]'
					connect={connect}
				/>
			)}
		</div>
	);
}

